/**
 * What becomes of each picture of a re-imported file (decision 64, decision 37; part 23b, table PI): one the export named
 * by an image of this book whose bytes, or failing that whose pixels, are still that image's keeps the image (PI1, PI2);
 * any other is found among the book's images by the hash of its bytes, else it is a new image, added only when the
 * writer applies the re-import (PI5). The file's name only says which of this book's images to compare with: the
 * reference written is always the book's own record's (PI11). Each lookup is injected, so the rows run in tests.
 */
import { imageDestination, readImageDestination, toHex, UNHELD_PICTURE } from '@cockatrice/core';
import type { ImageReference } from '@cockatrice/core';
import type { ReimportedPictureFile } from '../import/protocol.ts';
import type { StoredPicture } from './reimport.types.ts';

/** What deciding needs: the book's images and the hashing and comparing done elsewhere. */
export interface PictureLookup {
  /** The image the book stores under `id`, its bytes checked against its record (PI1); null for none. */
  readonly stored: (id: string) => Promise<StoredPicture | null>;
  /** The book's image whose bytes' SHA-256 (hex) is `sha256`, or null (PI5). */
  readonly byHash: (sha256: string) => Promise<ImageReference | null>;
  /** SHA-256 of `bytes`. */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** PX in the image worker: whether `file` shows what `stored` does; false for any failure (PI8). */
  readonly same: (file: Blob, stored: Uint8Array) => Promise<boolean>;
}

/**
 * Table PI's outcome for one picture: the book's image it is (kept, PI1, PI2, PI7; or found by its hash, PI5), a new
 * image to add from `file` at apply (PI5), or none, the file holding no picture to take (PI6).
 */
export type PictureDecision =
  | { readonly kind: 'kept' | 'found'; readonly destination: string }
  | { readonly kind: 'new'; readonly file: Blob }
  | { readonly kind: 'unheld' };

/** PI5: the book's image with the hash of `file`'s bytes, else a new image from it; null when the bytes can't be read. */
async function byBytes(file: Blob, bytes: Uint8Array | null, lookup: PictureLookup): Promise<PictureDecision> {
  const read = bytes ?? new Uint8Array(await file.arrayBuffer().catch(() => new ArrayBuffer(0)));
  const found = read.length === 0 ? null : await lookup.byHash(toHex(await lookup.digest(read)));
  return found === null ? { kind: 'new', file } : { kind: 'found', destination: imageDestination(found) };
}

/** Table PI: what becomes of `picture`. */
export async function decidePicture(picture: ReimportedPictureFile, lookup: PictureLookup): Promise<PictureDecision> {
  const named = picture.name === null ? null : readImageDestination(picture.name);
  const stored = named === null ? null : await lookup.stored(named.id);
  const { file } = picture;
  if (stored !== null) {
    const kept: PictureDecision = { kind: 'kept', destination: imageDestination(stored.reference) };
    // PI7: a part the export named that can't be read keeps the book's image.
    if (file === null) return kept;
    const bytes = new Uint8Array(await file.arrayBuffer().catch(() => new ArrayBuffer(0)));
    // PI1: the very bytes stored; PI2: Word's recompression of them.
    if (bytes.length > 0 && toHex(await lookup.digest(bytes)) === stored.reference.sha256) return kept;
    if (await lookup.same(file, stored.bytes)) return kept;
    // PI3: another picture under the name (Change Picture).
    return byBytes(file, bytes, lookup);
  }
  // PI6: a picture the file doesn't hold (or whose part it can't give) and the book doesn't either.
  if (file === null) return { kind: 'unheld' };
  // PI4, PI5: an editor's picture, or a name this book holds no image under.
  return byBytes(file, null, lookup);
}

/** A picture of the file and what becomes of it. */
export interface DecidedPicture {
  readonly picture: ReimportedPictureFile;
  readonly decision: PictureDecision;
}

/** Each picture's placeholder settled as the merge compares and writes it: the image's destination, the shared mark, or itself (a new image). */
export function settlement(decided: readonly DecidedPicture[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const { picture, decision } of decided) {
    if (decision.kind === 'kept' || decision.kind === 'found') out.set(picture.key, decision.destination);
    else if (decision.kind === 'unheld') out.set(picture.key, UNHELD_PICTURE);
  }
  return out;
}
